'use client';

import { Dialog } from '@base-ui/react/dialog';
import { css } from '@repo/pandacss/css';
import { getOrCreateId } from '@repo/platform-utils/static-ids';
import { CloseIcon } from '@repo/ui/icons/CloseIcon';
import { useRef } from 'react';
import { DialogRoot } from '@/shared/ui/DialogRoot/DialogRoot';
import { useBranchId } from '../../model/Context/hooks';
import { useIsExitIntentEnabled } from '../../model/exit-intent/Context';
import { useBranchedPageAction } from '../../model/useBranchedPageAction';
import { ExitModal } from '../exit-intent/ExitModal';
import { useExitModalCloseAction } from '../exit-intent/ExitModal/useExitModalCloseAction';

export function CloseFlow({
  desktopOnly = false,
  isDarkVariant = false,
}: {
  desktopOnly?: boolean;
  isDarkVariant?: boolean;
}) {
  const isExitIntentEnabled = useIsExitIntentEnabled();
  const openActivityIdRef = useRef('');
  const openActivityId = getOrCreateId(openActivityIdRef);
  const fireClosePageActionAnalyticsEvent = useBranchedPageAction({
    actionName: 'cardfinder_flow_exit',
    actionType: 'select',
    actionInput: 'exit',
    activityId: openActivityId,
  });

  const branchId = useBranchId();
  const { featureViewedActivityId, fireCloseAction } = useExitModalCloseAction(branchId);

  if (isExitIntentEnabled) return null;

  return (
    <DialogRoot
      // trap-focus: keep focus in the dialog without locking document scroll (avoids scrollbar layout shift).
      modal="trap-focus"
      onOpenChange={open => {
        if (!open) {
          fireCloseAction();
        }
      }}
    >
      <Dialog.Trigger
        render={
          <button
            type="button"
            className={css({
              width: '[20px]',
              height: '[20px]',
              lg: {
                position: 'absolute',
                top: '[25px]',
                right: '[25px]',
                width: '[24px]',
                height: '[24px]',
              },
              cursor: 'pointer',
              border: 'none',
              rounded: 'full',
              color: {
                base: 'neutral.darker',
                lg: isDarkVariant ? 'neutral.darker' : 'common.white',
              },
              px: '[0]',
              bg: '[transparent]',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              _hover: {
                bg: {
                  base: 'neutral.lightest',
                  lg: isDarkVariant ? 'neutral.lightest' : 'greens.dark',
                },
              },
              ...(desktopOnly && {
                display: { base: 'none', lg: 'flex' },
              }),
            })}
            aria-label="Close credit card finder"
            onClick={() => fireClosePageActionAnalyticsEvent()}
          >
            <CloseIcon fill="currentColor" />
          </button>
        }
      />
      <ExitModal featureViewedActivityId={featureViewedActivityId} branchId={branchId} />
    </DialogRoot>
  );
}
